import { useMutation } from '@apollo/client';
import {
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Select,
  Stack,
  useNotifications,
} from '@sixfold/common-ui';
import React, { useEffect, useState } from 'react';

import {
  CompanyIdentifierType,
  CreateCompanyIdentifierMutation,
  CreateCompanyIdentifierMutationVariables,
} from '../../lib/graphql';
import { companyQuery, createCompanyIdentifierMutation } from '../graphql';

type Props = {
  companyId: string;
  trigger: React.ReactElement | string;
};

/**
 * These are the known VAT country codes and their corresponding ISO 3166-1 alpha-2 codes.
 * @see https://en.wikipedia.org/wiki/VAT_identification_number
 */
const knownVATCountryCodesToIso3166_1Alpha2 = new Map([
  ['AT', ['AT']],
  ['BE', ['BE']],
  ['BG', ['BG']],
  ['HR', ['HR']],
  ['CY', ['CY']],
  ['CZ', ['CZ']],
  ['DK', ['DK']],
  ['EL', ['GR']], // Greece
  ['EE', ['EE']],
  ['FI', ['FI']],
  ['FR', ['FR', 'MC']], // France or Monaco
  ['DE', ['DE']],
  ['HU', ['HU']],
  ['IE', ['IE']],
  ['IT', ['IT']],
  ['LV', ['LV']],
  ['LT', ['LT']],
  ['LU', ['LU']],
  ['MT', ['MT']],
  ['NL', ['NL']],
  ['PL', ['PL']],
  ['PT', ['PT']],
  ['RO', ['RO']],
  ['SK', ['SK']],
  ['SI', ['SI']],
  ['ES', ['ES']],
  ['SE', ['SE']],
  ['GB', ['GB']],
]);

function extractVatCountryCode(value: string | undefined) {
  if (value === undefined || value.length < 2) {
    return undefined;
  }
  return value.slice(0, 2).toUpperCase();
}

export const CompanyIdentifierAddModal = (props: Props) => {
  const notify = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<CompanyIdentifierType>(CompanyIdentifierType.MISCELLANEOUS);
  const [value, setValue] = useState<string>();
  const [createCompanyIdentifier] = useMutation<
    CreateCompanyIdentifierMutation,
    CreateCompanyIdentifierMutationVariables
  >(createCompanyIdentifierMutation);

  const onToggle = () => {
    setValue('');
    setIsOpen(!isOpen);
  };

  const vatCountryCode = extractVatCountryCode(value);
  const isFilteredCountryCode =
    vatCountryCode !== undefined && knownVATCountryCodesToIso3166_1Alpha2.has(vatCountryCode);
  const filteredCountryCodes: string[] = isFilteredCountryCode
    ? (knownVATCountryCodesToIso3166_1Alpha2.get(vatCountryCode) ?? [])
    : Array.from(knownVATCountryCodesToIso3166_1Alpha2.values()).flat();

  const firstCountryCode: string | undefined = filteredCountryCodes[0];
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(
    isFilteredCountryCode ? firstCountryCode : undefined,
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRegion(isFilteredCountryCode ? firstCountryCode : undefined);
  }, [firstCountryCode, isFilteredCountryCode]);

  const handleConfirm = async () => {
    try {
      await createCompanyIdentifier({
        variables: {
          input: {
            companyId: props.companyId,
            identifierType: selectedType,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            identifierValue: value!.trim().toUpperCase(),
            discriminatorCountry: selectedRegion,
          },
        },
        refetchQueries: [
          {
            query: companyQuery,
            variables: {
              company_id: props.companyId,
            },
          },
        ],
      });
      onToggle();
      notify.success({ title: 'Company identifier successfully created.' });
    } catch (_error) {
      notify.error({ title: 'Creating the identifier failed.' });
    }
  };

  return (
    <Modal trigger={props.trigger} open={isOpen} onToggle={onToggle}>
      <ModalHeader title={'Add company identifier'} />
      <ModalMain>
        <Stack direction="column" gap="medium">
          <Select
            label="Identifier type"
            labelPosition="block"
            size="large"
            usePortal
            options={[
              { value: CompanyIdentifierType.VAT_NUMBER, label: 'VAT Number' },
              { value: CompanyIdentifierType.SCAC, label: 'SCAC' },
              { value: CompanyIdentifierType.MC_NUMBER, label: 'MC Number' },
              { value: CompanyIdentifierType.USDOT_NUMBER, label: 'USDOT Number' },
              { value: CompanyIdentifierType.TTC_SHARING_AGREEMENT_ID, label: 'TTC Sharing Agreement ID' },
              { value: CompanyIdentifierType.MISCELLANEOUS, label: 'Miscellaneous' },
            ]}
            value={selectedType}
            onChange={(option) => {
              if (option !== null) {
                setSelectedType(option.value as CompanyIdentifierType);
              }
            }}
          />
          <Input
            onChange={(event) => {
              setValue(event.value);
            }}
            value={value}
            errorMessage={undefined}
            label="Identifier value"
            name="identifier_value"
          />
          {selectedType === CompanyIdentifierType.VAT_NUMBER && (
            <Select
              label="Country code"
              labelPosition="block"
              isClearable
              usePortal
              placeholder="None"
              options={filteredCountryCodes.map((countryCode) => ({
                value: countryCode,
                label: countryCode,
              }))}
              value={selectedRegion ?? null}
              onChange={(option) => {
                setSelectedRegion(option === null ? undefined : option.value);
              }}
              disabled={value === undefined || value.length < 2}
            />
          )}
        </Stack>
      </ModalMain>
      <ModalFooter>
        <Stack justifyContent="end">
          <Button kind="subtle" onClick={onToggle}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={value === undefined || value.trim().length === 0} kind="primary">
            {'Add'}
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};
